// Render the resolved app summary and the multi-region comparison table with
// cross-region contrast highlights (Milestone M2).
//
// All values that originate from the Apple API are escaped before they touch
// innerHTML. Sorting is handled in this module via delegated click handlers on
// the table header; the contrast math itself lives in domain/contrast.js.

import { computeContrast, TAGS } from '../domain/contrast.js';
import { bestChartRank } from '../domain/chartRank.js';
import { isChartEnrichmentEnabled } from '../config.js';
import { t, getLocale, countryName } from '../i18n/index.js';
import { genreName } from '../data/genres.js';
import { rankByRatingCount } from '../domain/topCountries.js';
import { rowsToCsv, downloadCsv, buildCsvFilename } from '../domain/exportCsv.js';
import { rankByPopularity } from '../domain/popularityScore.js';
import { annotateWithOpportunity } from '../domain/opportunitySignal.js';
import { buildReport, downloadReport, buildReportFilename } from '../domain/reportExport.js';
import { mergeComparisonRows, sortMergedRows } from '../domain/compareApp.js';
import { deviationSortValue, DEVIATION_VARIANTS } from '../domain/ratingDeviation.js';
import { mountShareButton } from './shareButton.js';

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Render the resolved app header (artwork, name, seller, bundle id).
 * @param {object} app - Raw iTunes Search result.
 */
export function renderAppSummary(app) {
  const node = document.getElementById('app-summary');
  if (!node) return;

  if (!app) {
    node.hidden = true;
    node.innerHTML = '';
    currentAppName = null;
    currentApp = null;
    return;
  }

  currentAppName = app.trackName ?? null;
  currentApp = app;

  const artwork = app.artworkUrl100 || app.artworkUrl60 || '';
  node.hidden = false;
  node.innerHTML = `
    ${artwork ? `<img class="app-summary__art" src="${escapeHtml(artwork)}" alt="" width="80" height="80" />` : ''}
    <div class="app-summary__meta">
      <h2 class="app-summary__name">${escapeHtml(app.trackName)}</h2>
      <p class="app-summary__seller">${escapeHtml(app.sellerName || '')}</p>
      <p class="app-summary__ids">
        <span>trackId: ${escapeHtml(app.trackId)}</span>
        ${app.bundleId ? `<span>bundleId: ${escapeHtml(app.bundleId)}</span>` : ''}
      </p>
    </div>
  `;

  if (app.trackId) {
    mountShareButton(node, { appId: app.trackId, appName: app.trackName || '', t });
  }
}

function formatRating(row, contrast) {
  if (row.averageUserRating === null) return '—';
  const entry = contrast?.byRegion.get(row.countryCode);
  const dev = entry?.ratingDeviation;
  let indicator = '';
  if (dev != null && Math.abs(dev) >= 0.5) {
    const sign = dev > 0 ? '+' : '';
    const cls = dev > 0 ? 'rating-above' : 'rating-below';
    const absVal = Math.abs(dev).toFixed(2);
    const ariaKey = dev > 0 ? 'a11y.ratingAbove' : 'a11y.ratingBelow';
    const ariaLabel = escapeHtml(t(ariaKey, { value: absVal }));
    indicator = ` <span class="rating-dev ${cls}" title="${sign}${dev.toFixed(2)} vs global avg" aria-label="${ariaLabel}">${dev > 0 ? '▲' : '▼'}</span>`;
  }
  return `★ ${row.averageUserRating.toFixed(1)}${indicator}`;
}

function formatRatingCount(row) {
  if (row.userRatingCount === null) return '—';
  return row.userRatingCount.toLocaleString();
}

/**
 * Render the explicit "rating vs global average" deviation cell (issue #148).
 * Shows the signed deviation (e.g. "+0.80", "-1.20") tinted up/down, or "—" when
 * there is no comparable deviation — the rating is missing, or fewer than two
 * storefronts carry a rating so a cross-region mean is meaningless. The
 * deviation math lives in domain/contrast.js; this only formats the value
 * already attached to the contrast entry.
 */
function formatDeviation(row, contrast) {
  const dev = contrast?.byRegion.get(row.countryCode)?.ratingDeviation;
  if (dev === null || dev === undefined) return '<span class="muted">—</span>';
  const cls = dev > 0 ? 'deviation deviation--up' : dev < 0 ? 'deviation deviation--down' : 'deviation';
  const label = `${dev > 0 ? '+' : ''}${dev.toFixed(2)}`;
  return `<span class="${cls}">${escapeHtml(label)}</span>`;
}

function formatPrice(row) {
  if (row.formattedPrice) return escapeHtml(row.formattedPrice);
  if (row.price === 0) return escapeHtml(t('cell.free'));
  if (row.price !== null) return escapeHtml(String(row.price));
  return '—';
}

/**
 * Render the chart-rank cell for a row. When chart enrichment is disabled
 * (default static build), every row shows "—" with a tooltip explaining that
 * live ranks need a proxy. When enabled, shows the chart label, styling an
 * actual chart hit so it stands out.
 */
function formatChartRank(row) {
  if (!isChartEnrichmentEnabled()) {
    return `<span class="muted" title="${escapeHtml(t('chart.disabledTooltip'))}">—</span>`;
  }
  if (!row.chartLabel) {
    return `<span class="muted" title="${escapeHtml(t('chart.notLoaded'))}">—</span>`;
  }
  if (row.chartUnavailable) {
    return `<span class="muted" title="${escapeHtml(t('chart.unavailableTooltip'))}">${escapeHtml(row.chartLabel)}</span>`;
  }
  const charting = bestChartRank(row) !== null;
  const cls = charting ? 'chart-rank chart-rank--hit' : 'chart-rank muted';
  return `<span class="${cls}">${escapeHtml(row.chartLabel)}</span>`;
}

/**
 * Render the popularity score cell. Shows "score #rank ▲/▼delta" for available
 * rows that have been scored, "—" for unavailable / unscored rows.
 * Reads optional row.popularityDelta ({value, snappedAt}) for movement indicator.
 */
function formatPopularityScore(row) {
  if (row.popularityScore === null || row.popularityScore === undefined) {
    return '<span class="muted">—</span>';
  }
  const rankLabel = row.popularityRank !== null ? ` <span class="popularity-rank">#${escapeHtml(String(row.popularityRank))}</span>` : '';
  let deltaLabel = '';
  const d = row.popularityDelta;
  if (d && d.value !== 0) {
    const dateStr = escapeHtml(d.snappedAt ? d.snappedAt.slice(0, 10) : '');
    const abs = Math.abs(d.value);
    if (d.value > 0) {
      const ariaLabel = escapeHtml(t('delta.ariaUp', { n: String(abs), date: dateStr }));
      deltaLabel = ` <span class="pop-delta pop-delta--up" aria-label="${ariaLabel}" title="${ariaLabel}">${escapeHtml(t('delta.up', { n: String(abs) }))}</span>`;
    } else {
      const ariaLabel = escapeHtml(t('delta.ariaDown', { n: String(abs), date: dateStr }));
      deltaLabel = ` <span class="pop-delta pop-delta--down" aria-label="${ariaLabel}" title="${ariaLabel}">${escapeHtml(t('delta.down', { n: String(abs) }))}</span>`;
    }
  }
  return `<span class="popularity-score">${escapeHtml(String(row.popularityScore))}</span>${rankLabel}${deltaLabel}`;
}

// --- Sorting ---------------------------------------------------------------

// labelKey resolves through i18n at render time so headers re-translate on a
// language switch. The `key` is the stable identifier used by the sort logic.
const COLUMNS = [
  { key: 'country', labelKey: 'col.region', sortable: true },
  { key: 'rating', labelKey: 'col.rating', sortable: true, numeric: true },
  { key: 'ratingCount', labelKey: 'col.ratingCount', sortable: true, numeric: true },
  { key: 'ratingDeviation', labelKey: 'col.ratingDeviation', sortable: true, numeric: true },
  { key: 'price', labelKey: 'col.price', sortable: true, numeric: true },
  { key: 'chartRank', labelKey: 'col.chartRank', sortable: true, numeric: true },
  { key: 'popularityScore', labelKey: 'col.popularityScore', sortable: true, numeric: true },
  { key: 'highlights', labelKey: 'col.highlights', sortable: false },
  { key: 'store', labelKey: 'col.store', sortable: false },
];

// Default: most traction first.
let sortState = { key: 'ratingCount', dir: 'desc' };
let currentRows = [];
let currentAppName = null;
let currentApp = null;

/**
 * Return a comparator value for a row on a given sort key. Available rows always
 * sort ahead of unavailable/errored ones regardless of direction, so the table
 * never buries the rows that actually have data.
 *
 * `contrast` carries the cross-region facts (from computeContrast) so the
 * rating-deviation key can read each row's deviation from the global average
 * without recomputing it.
 */
function sortValue(row, key, contrast) {
  switch (key) {
    case 'country':
      return row.countryName ?? row.countryCode ?? '';
    case 'rating':
      return numericKey(row.averageUserRating);
    case 'ratingCount':
      return numericKey(row.userRatingCount);
    case 'ratingDeviation':
      // Signed deviation from the global average rating: desc surfaces the
      // strongest over-performers, asc the strongest under-performers. Rows
      // without a comparable deviation return null and so sort last.
      return deviationSortValue(
        contrast?.byRegion.get(row.countryCode)?.ratingDeviation ?? null,
        DEVIATION_VARIANTS.SIGNED,
      );
    case 'price':
      return numericKey(row.price);
    case 'chartRank':
      // Lower is better; not-charting / unavailable / disabled sort last.
      return numericKey(bestChartRank(row));
    case 'popularityScore':
      // Higher is better; rows with null score sort last.
      return numericKey(row.popularityScore ?? null);
    default:
      return 0;
  }
}

function numericKey(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function sortRows(rows, { key, dir }, contrast) {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aAvail = a.available && !a.error;
    const bAvail = b.available && !b.error;
    if (aAvail !== bAvail) return aAvail ? -1 : 1;

    const av = sortValue(a, key, contrast);
    const bv = sortValue(b, key, contrast);

    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av).localeCompare(String(bv)) * (dir === 'asc' ? 1 : -1);
    }
    // Nulls always sort last regardless of direction.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * factor;
  });
}

// --- Rendering -------------------------------------------------------------

function renderHeader() {
  return COLUMNS.map((col) => {
    const label = t(col.labelKey);
    if (!col.sortable) {
      return `<th scope="col">${escapeHtml(label)}</th>`;
    }
    const active = sortState.key === col.key;
    const dir = active ? sortState.dir : 'none';
    const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
    const arrow = active ? (dir === 'asc' ? ' ▲' : ' ▼') : '';
    return `<th scope="col" aria-sort="${ariaSort}">
      <button type="button" class="sort-btn" data-sort-key="${escapeHtml(col.key)}">${escapeHtml(label)}${arrow}</button>
    </th>`;
  }).join('');
}

function renderBadges(tags, entry) {
  if (!tags || tags.length === 0) return '<span class="muted">—</span>';
  return tags
    .map((tag) => {
      const attrs = [`class="badge badge--${escapeHtml(tag)}"`];
      let label = t(`tag.${tag}`);
      if (tag === TAGS.LOCALIZATION_GAP && entry?.localizationGap) {
        const language = entry.localizationGap.missingLanguages.join('/');
        label = t('tag.localization-gap', { language });
        attrs.push(`title="${escapeHtml(t('tag.localization-gap.tooltip', { language }))}"`);
      }
      return `<span ${attrs.join(' ')}>${escapeHtml(label)}</span>`;
    })
    .join(' ');
}

function renderProxyBar(share) {
  const pct = Math.max(0, Math.min(100, Math.round(share * 100)));
  return `<span class="proxy-bar" title="${escapeHtml(t('proxyBar.title', { pct }))}" aria-hidden="true"><span class="proxy-bar__fill" style="width:${pct}%"></span></span>`;
}

function renderStoreLink(row, name) {
  if (!row.storeUrl) return '<span class="muted">—</span>';
  const ariaLabel = escapeHtml(t('a11y.openStoreAriaLabel', { country: name }));
  return `<a href="${escapeHtml(row.storeUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${ariaLabel}">${escapeHtml(t('cell.open'))}</a>`;
}

/**
 * Render a small secondary line of extended metadata beneath the country name.
 * Shows only the fields that are present; returns empty string when none available.
 */
function renderMetaLine(row) {
  const parts = [];
  if (row.fileSize) parts.push(escapeHtml(row.fileSize));
  if (row.contentRating) parts.push(escapeHtml(row.contentRating));
  if (row.minimumOsVersion) parts.push(`iOS ${escapeHtml(row.minimumOsVersion)}+`);
  if (row.languageCount != null) parts.push(`${row.languageCount} lang`);
  if (row.releaseDate) parts.push(escapeHtml(row.releaseDate));
  if (parts.length === 0) return '';
  return `<span class="row-meta">${parts.join(' · ')}</span>`;
}

/**
 * Region cell: a mono country-code chip + the localized country name (with the
 * optional secondary metadata line beneath it). Flags-as-emoji are intentionally
 * gone — the chip reads consistently across platforms and matches the redesign.
 */
function renderRegionCell(row, name) {
  const cc = escapeHtml(String(row.countryCode).toUpperCase());
  return `<span class="region"><span class="cc">${cc}</span><span class="region__text"><span class="region__name">${escapeHtml(name)}</span>${renderMetaLine(row)}</span></span>`;
}

function renderRow(row, contrast) {
  const entry = contrast.byRegion.get(row.countryCode);
  const tags = entry?.tags ?? [];
  const name = countryName(row.countryCode, row.countryName);
  const region = renderRegionCell(row, name);

  if (row.error) {
    return `
      <tr class="row row--error">
        <th scope="row">${region}</th>
        <td colspan="8" class="cell-error">${escapeHtml(t('cell.lookupError', { message: row.error }))}</td>
      </tr>`;
  }
  if (!row.available) {
    return `
      <tr class="row row--unavailable">
        <th scope="row">${region}</th>
        <td colspan="7" class="cell-unavailable">${escapeHtml(t('cell.unavailable'))}</td>
        <td>${renderBadges(tags, entry)}</td>
      </tr>`;
  }

  const opportunityBadge = row.opportunity?.isOpportunity
    ? `<span class="badge badge--opportunity" title="${escapeHtml(t('opportunity.tooltip'))}" aria-label="${escapeHtml(t('a11y.opportunityAriaLabel'))}">${escapeHtml(t('opportunity.label'))}</span>`
    : '';

  return `
    <tr class="row${row.opportunity?.isOpportunity ? ' row--opportunity' : ''}">
      <th scope="row">
        ${region}
        ${opportunityBadge}
      </th>
      <td>${formatRating(row, contrast)}</td>
      <td class="cell-count">
        <span class="count-value">${formatRatingCount(row)}</span>
        ${renderProxyBar(entry?.ratingCountShare ?? 0)}
      </td>
      <td class="cell-deviation">${formatDeviation(row, contrast)}</td>
      <td>${formatPrice(row)}</td>
      <td class="cell-chart">${formatChartRank(row)}</td>
      <td class="cell-popularity">${formatPopularityScore(row)}</td>
      <td>${renderBadges(tags, entry)}</td>
      <td>${renderStoreLink(row, name)}</td>
    </tr>`;
}

/**
 * Render the "Top Markets" ranking summary panel.
 * Returns an HTML string — empty string if no data is available.
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rows
 * @returns {string}
 */
function renderRankingSummary(rows) {
  const top = rankByRatingCount(rows, 5);
  if (top.length === 0) return '';

  const items = top
    .map(({ rank, countryCode, countryName: fallback, value }) => {
      const label = countryName(countryCode, fallback);
      const formatted = value >= 1_000_000
        ? `${(value / 1_000_000).toFixed(1)}M`
        : value >= 1_000
        ? `${(value / 1_000).toFixed(0)}K`
        : String(value);
      return `<li class="ranking__item">
        <span class="ranking__rank" aria-hidden="true">${rank}</span>
        <span class="cc">${escapeHtml(String(countryCode).toUpperCase())}</span>
        <span class="ranking__country">${escapeHtml(label)}</span>
        <span class="ranking__count">${escapeHtml(formatted)}</span>
      </li>`;
    })
    .join('');

  return `<div class="ranking">
    <p class="ranking__title">${escapeHtml(t('ranking.title'))}</p>
    <ol class="ranking__list">${items}</ol>
  </div>`;
}

/**
 * Build the localized one-line insight from the contrast result's leader/laggard
 * codes. Composed here (not in domain/contrast.js) so it can be translated and
 * use localized country names while keeping the contrast math pure.
 * @param {import('../domain/contrast.js').ContrastResult} contrast
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rows
 * @returns {string}
 */
function buildInsightText(contrast, rows) {
  const label = (code) => {
    const row = rows.find((r) => r.countryCode === code);
    const name = countryName(code, row?.countryName);
    return `${name} (${String(code).toUpperCase()})`;
  };

  const parts = [];
  if (contrast.mostTractionCode) {
    parts.push(t('insight.most', { region: label(contrast.mostTractionCode) }));
  }
  if (contrast.weakestCode) {
    parts.push(t('insight.weakest', { region: label(contrast.weakestCode) }));
  }
  if (parts.length === 0) return t('insight.none');
  return `${parts.join(' · ')}. ${t('insight.suffix')}`;
}

/**
 * Render the per-region comparison table with contrast highlights.
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rows
 */
export function renderResultsTable(rows) {
  const node = document.getElementById('results');
  if (!node) return;

  // rankByPopularity adds popularityScore + popularityRank to each row so the
  // column renders correctly across all render calls (streaming, final, post-chart).
  // annotateWithOpportunity then adds the opportunity signal (⚡ early-mover flag).
  currentRows = Array.isArray(rows)
    ? annotateWithOpportunity(rankByPopularity(rows))
    : [];

  if (currentRows.length === 0) {
    node.innerHTML = '';
    return;
  }

  const contrast = computeContrast(currentRows);
  const ordered = sortRows(currentRows, sortState, contrast);
  const body = ordered.map((row) => renderRow(row, contrast)).join('');

  const chartNote = isChartEnrichmentEnabled()
    ? t('chartNote.enabled')
    : t('chartNote.disabled');

  node.innerHTML = `
    ${renderRankingSummary(currentRows)}
    <div class="insight" role="note">
      <svg class="insight__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M12 2a7 7 0 0 0-4 12.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3A7 7 0 0 0 12 2zM9 21h6"/></svg>
      <p class="insight__text">${escapeHtml(buildInsightText(contrast, currentRows))}</p>
    </div>
    <p class="chart-note muted" role="note">${escapeHtml(chartNote)}</p>
    <div class="table-scroll">
      <table class="results-table">
        <caption>${escapeHtml(t('table.caption'))}</caption>
        <thead><tr>${renderHeader()}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
    <div class="export-row">
      <button type="button" class="export-csv-btn">${escapeHtml(t('export.csv'))}</button>
      <button type="button" class="export-report-btn">${escapeHtml(t('export.report'))}</button>
    </div>
  `;

  wireSorting(node);
  wireExport(node);
}

let wired = false;
function wireSorting(node) {
  if (wired) return;
  wired = true;
  // Delegate so we survive re-renders without stacking listeners.
  node.addEventListener('click', (event) => {
    const btn = event.target.closest('.sort-btn');
    if (!btn) return;
    const key = btn.getAttribute('data-sort-key');
    if (!key) return;

    if (sortState.key === key) {
      sortState = { key, dir: sortState.dir === 'asc' ? 'desc' : 'asc' };
    } else {
      // Text columns default to ascending; numeric columns to descending so the
      // "best" value lands at the top on first click.
      const col = COLUMNS.find((c) => c.key === key);
      sortState = { key, dir: col && col.numeric ? 'desc' : 'asc' };
    }
    renderResultsTable(currentRows);
  });
}

let exportWired = false;
function wireExport(node) {
  if (exportWired) return;
  exportWired = true;
  node.addEventListener('click', (event) => {
    if (event.target.closest('.export-csv-btn')) {
      if (currentRows.length === 0) return;
      const csv = rowsToCsv(currentRows);
      const today = new Date().toISOString().slice(0, 10);
      downloadCsv(csv, buildCsvFilename(currentAppName, today));
      return;
    }
    if (event.target.closest('.export-report-btn')) {
      if (currentRows.length === 0) return;
      const today = new Date().toISOString().slice(0, 10);
      const report = buildReport(currentApp, currentRows, new Date().toISOString());
      downloadReport(report, buildReportFilename(currentAppName, today));
    }
  });
}

export function clearResults() {
  const node = document.getElementById('results');
  if (node) node.innerHTML = '';
  currentRows = [];
  currentApp = null;
  exportWired = false;
  // Clear the developer apps panel so stale results from a previous search don't linger.
  const devNode = document.getElementById('developer-apps');
  if (devNode) {
    devNode.hidden = true;
    devNode.innerHTML = '';
  }
}

/**
 * Re-render the current table in place (e.g. after a language switch) without
 * needing the caller to hold onto the rows. No-op when nothing is shown.
 */
export function rerenderResults() {
  if (currentRows.length > 0) renderResultsTable(currentRows);
}

/**
 * Render (or clear) the "More apps by this developer" panel.
 * @param {object[]|null} apps - Array of raw iTunes results, or null to hide.
 * @param {string} developerName - Developer/seller name for the panel title.
 * @param {function(number|string): void} onCompare - Called with trackId when Compare is clicked.
 */
export function renderDeveloperApps(apps, developerName, onCompare) {
  const node = document.getElementById('developer-apps');
  if (!node) return;

  if (!apps || apps.length === 0) {
    node.hidden = true;
    node.innerHTML = '';
    return;
  }

  const title = escapeHtml(t('developer.otherApps', { developer: developerName }));
  const compareLabel = escapeHtml(t('developer.compare'));

  const cards = apps
    .map((app) => {
      const artwork = escapeHtml(app.artworkUrl100 || app.artworkUrl60 || '');
      const name = escapeHtml(app.trackName || '');
      // Localize by Apple genre id; fall back to the lookup's English genre name.
      const genre = app.primaryGenreName
        ? escapeHtml(genreName(app.primaryGenreId, getLocale(), app.primaryGenreName))
        : '';
      const tid = escapeHtml(String(app.trackId || ''));
      const compareAriaLabel = escapeHtml(t('developer.compareAriaLabel', { name: app.trackName || '' }));
      return `
        <li class="dev-apps__card">
          ${artwork ? `<img class="dev-apps__art" src="${artwork}" alt="" width="48" height="48" loading="lazy" />` : '<div class="dev-apps__art dev-apps__art--placeholder"></div>'}
          <div class="dev-apps__info">
            <span class="dev-apps__name">${name}</span>
            ${genre ? `<span class="dev-apps__genre">${genre}</span>` : ''}
          </div>
          <button class="dev-apps__compare-btn" type="button" data-track-id="${tid}" aria-label="${compareAriaLabel}">${compareLabel}</button>
        </li>`;
    })
    .join('');

  node.hidden = false;
  node.innerHTML = `<details class="dev-apps__details" open>
    <summary class="dev-apps__summary">${title}</summary>
    <ul class="dev-apps__list">${cards}</ul>
  </details>`;

  node.querySelectorAll('.dev-apps__compare-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const trackId = btn.dataset.trackId;
      if (trackId && onCompare) onCompare(trackId);
    });
  });
}

// ── Dual-app (cross-app) comparison table ────────────────────────────────────

/**
 * Render a side-by-side comparison of two apps across all storefronts.
 * Replaces the normal single-app table in #results.
 *
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rowsA
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rowsB
 * @param {object} appA - Raw iTunes result for App A.
 * @param {object} appB - Raw iTunes result for App B.
 */
export function renderDualAppTable(rowsA, rowsB, appA, appB) {
  const node = document.getElementById('results');
  if (!node) return;

  if (!rowsA.length && !rowsB.length) {
    node.innerHTML = '';
    return;
  }

  const merged = sortMergedRows(mergeComparisonRows(rowsA, rowsB));
  const nameA = escapeHtml(appA?.trackName ?? t('compare.col.appA'));
  const nameB = escapeHtml(appB?.trackName ?? t('compare.col.appB'));
  const na = escapeHtml(t('compare.unavailable'));

  function fmtRating(row) {
    if (!row || !row.available || row.error) return na;
    if (row.averageUserRating == null) return na;
    return escapeHtml(`★ ${row.averageUserRating.toFixed(1)}`);
  }

  function fmtCount(row) {
    if (!row || !row.available || row.error) return na;
    if (row.userRatingCount == null) return na;
    return escapeHtml(row.userRatingCount.toLocaleString());
  }

  function fmtStoreLink(row, country) {
    if (!row || !row.available || row.error || !row.storeUrl) return na;
    const ariaLabel = escapeHtml(t('a11y.openStoreAriaLabel', { country }));
    return `<a href="${escapeHtml(row.storeUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${ariaLabel}">${escapeHtml(t('cell.open'))}</a>`;
  }

  const tbody = merged.map(({ countryCode, countryName: cname, appA: ra, appB: rb }) => {
    const name = countryName(countryCode, cname);
    const region = `<span class="region"><span class="cc">${escapeHtml(String(countryCode).toUpperCase())}</span><span class="region__name">${escapeHtml(name)}</span></span>`;
    const aAvail = ra?.available && !ra?.error;
    const bAvail = rb?.available && !rb?.error;
    const rowCls = (!aAvail && !bAvail) ? ' class="row row--unavailable"' : ' class="row"';
    return `<tr${rowCls}>
      <th scope="row">${region}</th>
      <td>${fmtRating(ra)}</td>
      <td>${fmtCount(ra)}</td>
      <td>${fmtStoreLink(ra, name)}</td>
      <td class="dual-divider"></td>
      <td>${fmtRating(rb)}</td>
      <td>${fmtCount(rb)}</td>
      <td>${fmtStoreLink(rb, name)}</td>
    </tr>`;
  }).join('');

  node.innerHTML = `
    <div class="dual-app-header">
      <span class="dual-app-label dual-app-label--a">${nameA}</span>
      <span class="dual-app-vs">vs</span>
      <span class="dual-app-label dual-app-label--b">${nameB}</span>
    </div>
    <div class="table-scroll">
      <table class="results-table dual-app-table">
        <thead>
          <tr>
            <th scope="col">${escapeHtml(t('col.region'))}</th>
            <th scope="col">${escapeHtml(t('compare.col.ratingA'))}</th>
            <th scope="col">${escapeHtml(t('compare.col.ratingCountA'))}</th>
            <th scope="col">${escapeHtml(t('col.store'))}</th>
            <th scope="col" class="dual-divider" aria-hidden="true"></th>
            <th scope="col">${escapeHtml(t('compare.col.ratingB'))}</th>
            <th scope="col">${escapeHtml(t('compare.col.ratingCountB'))}</th>
            <th scope="col">${escapeHtml(t('col.store'))}</th>
          </tr>
        </thead>
        <tbody>${tbody}</tbody>
      </table>
    </div>`;
}
